import { useTheme } from '../../hooks/useTheme';
import { Wizard, WizardStep, Card, Input, Tile, Switch, TextArea } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useLoader } from '../../hooks/useLoader';
import { AxiosError } from 'axios';
import { Enm, SoftwareRepository, EiapCluster, AutomationFlow, SelectiveOnboardingConfig } from '../../types/automation-flow.types';
import {
  useRepositories,
  useEiapClusters,
  useEnms,
  useProductLines,
  useCnis,
  useMoshells,
  useMoshellNodes
} from '../../hooks/useAutomationData';

import { automationFlowService } from '../../services/automationFlowService';
import { getRepositoryTypeInfo, getStatusColor, RepositoryType } from '../../utils/repositoryTypes';
import { CnisEndpoint } from '../environments/CNIS/cnis-endpoint-interfaces';
import { MoshellEndpoint, PBNodesDeploy } from '../environments/Moshell/moshell-endpoint-interfaces';
import MoshellNodesList from '../environments/Moshell/MoshellNodesList';

type WizardMode = 'onboarding' | 'full';
export interface AutomationWizardProps {
  mode: WizardMode;
}

/** Default when loading a flow that has no selectiveOnboarding config (e.g. legacy flows). */
const DEFAULT_SELECTIVE_ONBOARDING_LOAD: SelectiveOnboardingConfig = {
  enabled: false
};

/** Normalize from API; ignores legacy labOnboardingMode if present. */
function normalizeSelectiveOnboarding(raw: unknown): SelectiveOnboardingConfig {
  if (!raw || typeof raw !== 'object') {
    return DEFAULT_SELECTIVE_ONBOARDING_LOAD;
  }
  const o = raw as Record<string, unknown>;
  const enabled = typeof o.enabled === 'boolean' ? o.enabled : DEFAULT_SELECTIVE_ONBOARDING_LOAD.enabled;
  return { enabled };
}

export default function AutomationWizard({ mode }: AutomationWizardProps) {
  const isFullMode = mode === 'full';
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotifications } = useSwobNotifications();
  const { showLoader, hideLoader } = useLoader();

  const templateState = location.state as {
    selectedTemplate?: string;
    preSelectedProductType?: string;
    artifactTypeRule?: string;
    flowId?: string;
    mode?: 'edit' | 'create';
    selectedCard?: { title?: string; wizardTitle?: string; backendTemplate?: string };
  } | null;
  const [preSelectedProductType, setPreSelectedProductType] = useState(templateState?.preSelectedProductType || '');
  const preSelectedArtifactRule = templateState?.artifactTypeRule;

  const { repositories, fetchRepositories } = useRepositories();
  const { eiapClusters, fetchEiapClusters } = useEiapClusters();
  const { cnis, fetchCnis } = useCnis();
  const { enms, fetchEnms } = useEnms();
  const { fetchMoshells } = useMoshells();
  const { moshellNodes, fetchMoshellNodes } = useMoshellNodes();
  const { productLines, fetchProductLines } = useProductLines();

  const [enmMoshells, setEnmMoshells] = useState<MoshellEndpoint[]>([]);

  const [formData, setFormData] = useState<{
    repository: string;
    productLine: string;
    products: string[];
    eiapCluster: string;
    enms: string[];
    cnisEndpoint: string;
    moshell: string;
    pbNodesDeploy: PBNodesDeploy[];
    deploymentAutomation: boolean;
    testAutomation: boolean;
    selectiveOnboarding: SelectiveOnboardingConfig;
    flowName: string;
    flowDescription: string;
  }>({
    repository: '',
    productLine: '',
    products: [],
    eiapCluster: '',
    enms: [],
    cnisEndpoint: '',
    moshell: '',
    pbNodesDeploy: [],
    deploymentAutomation: true,
    testAutomation: templateState?.preSelectedProductType === 'gNB',
    selectiveOnboarding: {
      enabled: true
    },
    flowName: '',
    flowDescription: ''
  });

  const [originalFlowName, setOriginalFlowName] = useState('');

  const [upgradeMethod, setUpgradeMethod] = useState('moshell');

  // Fetch all data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      showLoader();
      try {
        const [reposResult, clustersResult, enmsResult, cnisResult, moshellResult] = await Promise.all([
          fetchRepositories().catch((error) => {
            addNotifications([
              {
                title: 'Error - Software Repositories',
                icon: 'icon-triangle-warning',
                description:
                  error instanceof AxiosError
                    ? error.response?.data?.message || error.message
                    : 'An error occurred while fetching software repositories.',
                color: 'red',
                timestamp: new Date()
              }
            ]);
            return [];
          }),
          fetchEiapClusters().catch((error) => {
            addNotifications([
              {
                title: 'Error - EIAP Clusters',
                icon: 'icon-triangle-warning',
                description:
                  error instanceof AxiosError
                    ? error.response?.data?.message || error.message
                    : 'An error occurred while fetching EIAP clusters.',
                color: 'red',
                timestamp: new Date()
              }
            ]);
            return [];
          }),
          fetchEnms().catch((error) => {
            addNotifications([
              {
                title: 'Error - ENMs',
                icon: 'icon-triangle-warning',
                description:
                  error instanceof AxiosError ? error.response?.data?.message || error.message : 'An error occurred while fetching ENMs.',
                color: 'red',
                timestamp: new Date()
              }
            ]);
            return [];
          }),
          fetchCnis().catch((error) => {
            addNotifications([
              {
                title: 'Error - CNIS',
                icon: 'icon-triangle-warning',
                description:
                  error instanceof AxiosError ? error.response?.data?.message || error.message : 'An error occurred while fetching CNIS.',
                color: 'red',
                timestamp: new Date()
              }
            ]);
            return [];
          }),
          fetchMoshells().catch((error) => {
            addNotifications([
              {
                title: 'Error - Moshells',
                icon: 'icon-triangle-warning',
                description:
                  error instanceof AxiosError
                    ? error.response?.data?.message || error.message
                    : 'An error occurred while fetching Moshells.',
                color: 'red',
                timestamp: new Date()
              }
            ]);
            return [];
          })
        ]);

        if (!reposResult || reposResult.length === 0) {
          setFormData((prev) => ({ ...prev, repository: 'swgw' }));
        }
        if (clustersResult && clustersResult.length === 1) {
          setFormData((prev) => ({ ...prev, eiapCluster: clustersResult[0].id, enms: [] }));
        }
        if (cnisResult?.length === 1) {
          setFormData((prev) => ({ ...prev, cnisEndpoint: cnisResult[0].id }));
        }
        if (moshellResult?.lenth === 1) {
          setFormData((prev) => ({ ...prev, moshell: moshellResult[0].id }));
        }

        if (templateState?.mode === 'edit' && templateState?.flowId) {
          await loadAutomationFlow(
            templateState.flowId,
            reposResult || [],
            clustersResult || [],
            enmsResult || [],
            cnisResult || [],
            moshellResult || []
          );
        } else if (preSelectedProductType) {
          //await loadProductLinesData(preSelectedProductType);
        }
      } finally {
        setTimeout(() => {
          hideLoader();
        }, 500);
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProductLinesData = async (productTypeName: string) => {
    try {
      const result = await fetchProductLines(productTypeName);
      if (result.length > 0) {
        setFormData((prev) => ({ ...prev, productLine: result[0].name }));
      }
    } catch (error) {
      addNotifications([
        {
          title: 'Error - Product Lines',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to fetch product lines.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    }
  };

  const validateFlowData = (
    flow: AutomationFlow,
    fetchedRepos: SoftwareRepository[],
    fetchedClusters: EiapCluster[],
    fetchedEnms: Enm[],
    fetchedCnis: CnisEndpoint[],
    fetchedMoshells: MoshellEndpoint[]
  ) => {
    const repoId = flow.configuration?.repository;
    const repoExists = repoId === 'swgw' || fetchedRepos.some((r) => r.id === repoId);
    if (!repoExists && repoId) {
      addNotifications([
        {
          title: 'Warning - Software Repository',
          icon: 'icon-triangle-warning',
          description: 'The previously configured software repository is no longer available. Please select a new one.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    const savedEnmIds = flow.enms?.map((e: Enm) => e.id) || [];
    const validEnmIds = savedEnmIds.filter((id: string) => fetchedEnms.some((e) => e.id === id));
    if (validEnmIds.length < savedEnmIds.length && flow?.enms?.length !== 0) {
      addNotifications([
        {
          title: 'Warning - ENMs',
          icon: 'icon-triangle-warning',
          description: 'One or more previously configured ENMs are no longer available. Please review your selection.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    handleSelectEnm(validEnmIds, fetchedEnms);

    const eiapClusterId = flow.eiapClusterId || '';
    const eiapExists = !eiapClusterId || fetchedClusters.some((c) => c.id === eiapClusterId);
    if (!eiapExists) {
      addNotifications([
        {
          title: 'Warning - EIAP Cluster',
          icon: 'icon-triangle-warning',
          description: 'The previously configured EIAP Cluster is no longer available. Please select a new one.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    const cnisId = flow.cnisEndpointId;
    const cnisExists = !cnisId || fetchedCnis.some((c) => c.id === cnisId);
    if (!cnisExists) {
      addNotifications([
        {
          title: 'Warning - CNIS',
          icon: 'icon-triangle-warning',
          description: 'The previously configured CNIS Endpoint is no longer available. Please select a new one.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }
    const moshellId = flow.moshellId || '';
    const moshellExists = !moshellId || fetchedMoshells.some((m) => m.id === moshellId);
    if (!moshellExists) {
      addNotifications([
        {
          title: 'Warning - Moshell Endpoint',
          icon: 'icon-triangle-warning',
          description: 'The previously configured Moshell Endpoint is no longer available. Please select a new one.',
          color: 'yellow',
          timestamp: new Date()
        }
      ]);
    }

    return {
      repository: repoExists ? repoId : '',
      eiapCluster: eiapExists ? eiapClusterId : '',
      enms: validEnmIds,
      cnisEndpoint: cnisExists ? cnisId : '',
      moshell: moshellExists ? moshellId : ''
    };
  };

  const loadAutomationFlow = async (
    flowId: string,
    fetchedRepos: SoftwareRepository[],
    fetchedClusters: EiapCluster[],
    fetchedEnms: Enm[],
    fetchedCnis: CnisEndpoint[],
    fetchedMoshells: MoshellEndpoint[]
  ) => {
    try {
      const resp = await automationFlowService.getById(flowId);
      const flow = resp.data;
      console.log('Fetched automation flow:', flow);
      const validated = validateFlowData(flow, fetchedRepos, fetchedClusters, fetchedEnms, fetchedCnis, fetchedMoshells);
      setFormData({
        ...validated,
        productLine: flow.productLineId || '',
        products: [],
        deploymentAutomation: flow.configuration?.deploymentAutomation || false,
        testAutomation: flow.configuration?.testAutomation || false,
        selectiveOnboarding: normalizeSelectiveOnboarding(flow.configuration?.selectiveOnboarding),
        flowName: flow.name || '',
        flowDescription: flow.description || '',
        pbNodesDeploy: flow.pbNodesDeploy
      });

      setOriginalFlowName(flow.name || '');
      if (flow.productType && flow.productType.name) {
        setPreSelectedProductType(flow.productType.name);
        await loadProductLinesData(flow.productType.name);
      }
    } catch (error) {
      addNotifications([
        {
          title: 'Error - Automation Flow',
          icon: 'icon-triangle-warning',
          description:
            error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching automation flow.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    }
  };

  const selectedEiapCluster = eiapClusters.find((cluster) => cluster.id === formData.eiapCluster);

  const availableEnms = selectedEiapCluster ? enms.filter((enm) => enm.linkedEiapClusters.includes(selectedEiapCluster.id)) : enms;

  useEffect(() => {
    if (availableEnms.length === 1 && formData.enms.length === 0) {
      setFormData((prev) => ({ ...prev, enms: [availableEnms[0].id] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableEnms.length]);

  const handleCancel = () => {
    navigate('/automation-flows/new');
  };

  const defaultStageModel = isFullMode ? 'Full_Automation' : 'Onboarding_Only';

  const handleFinish = async () => {
    try {
      showLoader();

      if (!formData.flowName || !formData.flowName.trim()) {
        addNotifications([
          {
            title: 'Validation Error',
            icon: 'icon-triangle-warning',
            description: 'Automation Flow Name is required.',
            color: 'red',
            timestamp: new Date()
          }
        ]);
        hideLoader();
        return;
      }
      if (!formData.repository) {
        addNotifications([
          {
            title: 'Validation Error',
            icon: 'icon-triangle-warning',
            description: 'Please select a Software Repository.',
            color: 'red',
            timestamp: new Date()
          }
        ]);
        hideLoader();
        return;
      }
      if (preSelectedProductType !== 'CNIS' && preSelectedProductType !== 'Purpose Built' && !formData.eiapCluster) {
        addNotifications([
          {
            title: 'Validation Error',
            icon: 'icon-triangle-warning',
            description: 'Please select an EIAP Cluster.',
            color: 'red',
            timestamp: new Date()
          }
        ]);
        hideLoader();
        return;
      }
      if (preSelectedProductType !== 'CNIS' && preSelectedProductType !== 'Purpose Built' && formData.enms.length === 0) {
        addNotifications([
          {
            title: 'Validation Error',
            icon: 'icon-triangle-warning',
            description: 'Please select at least one ENM.',
            color: 'red',
            timestamp: new Date()
          }
        ]);
        hideLoader();
        return;
      }
      if (preSelectedProductType === 'CNIS' && !formData.cnisEndpoint) {
        addNotifications([
          {
            title: 'Validation Error',
            icon: 'icon-triangle-warning',
            description: 'Please select an CNIS Endpoint.',
            color: 'red',
            timestamp: new Date()
          }
        ]);
        hideLoader();
        return;
      }
      if (preSelectedProductType === 'Purpose Built' && !formData.moshell) {
        addNotifications([
          {
            title: 'Validation Error',
            icon: 'icon-triangle-warning',
            description: 'Please select an Moshell Endpoint.',
            color: 'red',
            timestamp: new Date()
          }
        ]);
        hideLoader();
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const configuration: Record<string, any> = {
        repository: formData.repository,
        repositoryType: formData.repository === 'swgw' ? 'swgw' : 'external',
        selectiveOnboarding: {
          enabled: formData.selectiveOnboarding.enabled
        }
      };
      if (isFullMode) {
        configuration.deploymentAutomation = formData.deploymentAutomation;
        configuration.testAutomation = formData.testAutomation;
      }

      const automationFlowData = {
        name: formData.flowName.trim(),
        description: formData.flowDescription.trim(),
        cardName: location.state?.selectedCard?.title || 'Unknown Card',
        productType: preSelectedProductType || 'gNB',
        stageModel: location.state?.selectedCard?.backendTemplate || defaultStageModel,
        artifactTypeRule: preSelectedArtifactRule || 'unverified',
        eiapClusterId: formData.eiapCluster || null,
        enmIds: formData.enms,
        cnisEndpointId: formData.cnisEndpoint || null,
        moshellId: formData.moshell || null,
        pbNodesDeploy: formData.pbNodesDeploy,
        configuration,
        status: 'active'
      };

      console.log('Sending automation flow data:', automationFlowData);

      if (templateState?.mode === 'edit' && templateState?.flowId) {
        const updatedData = {
          name: formData.flowName.trim(),
          description: formData.flowDescription.trim(),
          eiapClusterId: formData.eiapCluster || null,
          enmIds: formData.enms,
          cnisEndpointId: formData.cnisEndpoint || null,
          moshellId: formData.moshell || null,
          pbNodesDeploy: formData.pbNodesDeploy,
          configuration
        };
        await automationFlowService.update(templateState.flowId, updatedData);
      } else {
        await automationFlowService.create(automationFlowData);
      }

      addNotifications([
        {
          title: 'Success',
          icon: 'icon-check',
          description: templateState?.mode === 'edit' ? 'Automation Flow updated successfully!' : 'Automation Flow created successfully!',
          color: 'green',
          timestamp: new Date()
        }
      ]);

      navigate('/automation-flows');
    } catch (error: unknown) {
      addNotifications([
        {
          title: 'Error - Create Automation Flow',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while creating the automation flow.',
          color: 'red',
          timestamp: new Date()
        }
      ]);
    } finally {
      setTimeout(() => {
        hideLoader();
      }, 500);
    }
  };

  const selectedCard = location.state?.selectedCard;
  const baseTitle = selectedCard?.wizardTitle || (selectedCard?.title ? `Configure ${selectedCard.title}` : 'Configure Automation Flow');
  const wizardTitle = templateState?.mode === 'edit' && originalFlowName ? `${baseTitle}: ${originalFlowName}` : baseTitle;

  const validateStep3 = (): boolean => {
    if (preSelectedProductType === 'CNIS') {
      return !!formData.cnisEndpoint;
    } else if (preSelectedProductType === 'Purpose Built') {
      return !!formData.moshell;
    } else {
      return !!formData.eiapCluster && formData.enms.length > 0;
    }
  };
  const validateStep5 = (): boolean => {
    if (preSelectedProductType === 'Purpose Built') {
      return formData.pbNodesDeploy.length > 0;
    }
    return true;
  };
  const getMoshellNodes = async (id: string) => {
    if (id) {
      showLoader();
      const response = await fetchMoshellNodes(id).catch((error) => {
        addNotifications([
          {
            title: 'Error - Moshell',
            icon: 'icon-triangle-warning',
            description:
              error instanceof AxiosError
                ? error.response?.data?.message || error.message
                : 'An error occurred while fetching Moshell Nodes.',
            color: 'red',
            timestamp: new Date()
          }
        ]);
        hideLoader();
        return [];
      });
      if (response) {
        hideLoader();
      }
    }
  };

  const handleSelectNodes = (nodes: PBNodesDeploy[]) => {
    setFormData({ ...formData, pbNodesDeploy: nodes || [] });
  };

  const handleSelectEnm = (selectedEnms: string[], enmArray?: Enm[]) => {
    const enmArr = enmArray || enms;
    const arr: MoshellEndpoint[] = [];
    selectedEnms.forEach((e) => {
      const found = enmArr.find((enm) => enm.id === e);
      found?.moshellEnms?.forEach((me) => {
        if (me.moshellEndpoint) arr.push(me.moshellEndpoint);
      });
    });
    setEnmMoshells(arr);
  };

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
      <ReactEDSWrapper component={Tile} title={wizardTitle} id="configure-automation-flow-tile">
        <ReactEDSWrapper
          component={Wizard}
          onCancel={handleCancel}
          onFinish={handleFinish}
          nextBtnLabel="Next"
          previousBtnLabel="Back"
          cancelBtnLabel="Cancel"
          finishBtnLabel={templateState?.mode === 'edit' ? 'Update Automation Flow' : 'Create Automation Flow'}
        >
          {/* Step 1: Initial Information */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Initial Information" valid={!!formData.flowName}>
            <p style={{ fontSize: '0.9rem', color: '#8b8b8b', marginBottom: '1rem' }}>
              Provide the initial details to start setting up your workflow.
            </p>
            <div className="column sm-12">
              <ReactEDSWrapper
                component={Input}
                required
                label="Name"
                placeholder="Automation Flow 1"
                value={formData.flowName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, flowName: e.target.value })}
              />
            </div>
            <div className="column sm-12">
              <ReactEDSWrapper
                component={TextArea}
                label=" Description"
                placeholder="Automation Flow Description"
                value={formData.flowDescription}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, flowDescription: e.target.value })}
                style={{ width: '25%' }}
                maxLength={150}
              />
            </div>
          </ReactEDSWrapper>
          {/* Step 2: SW Repository Connection */}
          <ReactEDSWrapper component={WizardStep} stepTitle="SW Repository Connection" valid={!!formData.repository}>
            <p style={{ fontSize: '0.9rem', color: '#8b8b8b', marginBottom: '1rem' }}>
              Select one of the available software repository connections to use in this automation flow.
              <br />
              You can choose between the Ericsson Software Gateway or an External Repository.
            </p>

            <div className="row" style={{ marginBottom: '24px' }}>
              <div className="column sm-12">
                <h5>Ericsson Software Gateway</h5>
              </div>
              <div className="column sm-12 md-6 lg-4">
                <ReactEDSWrapper
                  component={Card}
                  title="Ericsson Software Gateway"
                  selected={formData.repository === 'swgw'}
                  onChange={(selected) => {
                    if (selected) {
                      setFormData({ ...formData, repository: 'swgw' });
                    } else {
                      setFormData({ ...formData, repository: '' });
                    }
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                    <span>Official Ericsson Software Distribution</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#6a6a6a' }}>
                    Connect to Ericsson's centralized software gateway for official product releases
                  </p>
                </ReactEDSWrapper>
              </div>
            </div>

            <div className="row">
              <div className="column sm-12">
                <h5>External Repositories</h5>
              </div>
              {repositories.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center' }}>
                  <p style={{ color: '#6a6a6a', marginBottom: '16px' }}>No external software repositories configured yet.</p>
                  <p style={{ color: '#6a6a6a' }}>
                    Please configure a software repository in <Link to="/sw-repositories/external-repositories">SW Repositories</Link>{' '}
                    first.
                  </p>
                </div>
              ) : (
                repositories.map((repository) => (
                  <div key={repository.id} className="column sm-12 md-6 lg-4">
                    <ReactEDSWrapper
                      component={Card}
                      title={repository.name}
                      subtitle={getRepositoryTypeInfo(repository.type as RepositoryType)?.name}
                      selected={formData.repository === repository.id}
                      onChange={(selected) => {
                        if (selected) {
                          setFormData({ ...formData, repository: repository.id });
                        } else {
                          setFormData({ ...formData, repository: '' });
                        }
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span>
                          <i className="icon icon-link" style={{ marginRight: '8px', fontSize: '18px' }} /> {repository.url}
                        </span>
                        <span>
                          <i className="icon icon-avatar" style={{ marginRight: '8px', fontSize: '18px' }} /> {repository.username}
                        </span>
                        <span>
                          <i
                            className="icon icon-alarm-level4"
                            style={{
                              fontSize: '8px',
                              color: getStatusColor(repository.status),
                              marginRight: '6px'
                            }}
                          />
                          <span style={{ fontSize: '11px', textTransform: 'capitalize', color: '#6a6a6a' }}>{repository.status}</span>
                        </span>
                      </div>
                    </ReactEDSWrapper>
                  </div>
                ))
              )}
            </div>
          </ReactEDSWrapper>

          {/* Step 2: Product Line */}
          {/*<ReactEDSWrapper component={WizardStep} stepTitle="Product Line" valid={!!formData.productLine}>
            <h3>Product Lines</h3>
            <p>List of product lines associated with the automation workflow.</p>

            <div className="row" style={{ marginBottom: '12px' }}>
              {productLines.length === 0 ? (
                <div className="column sm-12">
                  <div style={{ padding: '24px', textAlign: 'center' }}>
                    <p style={{ color: '#6a6a6a' }}>No product lines configured.</p>
                  </div>
                </div>
              ) : (
                productLines.map((productLine) => (
                  <div key={productLine.name} className="column sm-12 md-6 lg-3">
                    <ReactEDSWrapper component={Card} title={productLine.name} />
                  </div>
                ))
              )}
            </div>
          </ReactEDSWrapper>*/}

          {/* Step 3: Target Environment */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Target Environment" valid={validateStep3()}>
            {preSelectedProductType !== 'CNIS' && preSelectedProductType !== 'Purpose Built' && (
              <div>
                <h3>
                  Select Target Environment <span style={{ color: '#0069C2' }}> *</span>
                </h3>
                <p>Select the EIAP Cluster and the linked ENMs where SW onboarding will execute.</p>

                <div className="row" style={{ marginBottom: '24px' }}>
                  <div className="column sm-12">
                    <h4>Select EIAP Cluster</h4>
                  </div>
                  {eiapClusters.length === 0 ? (
                    <div className="column sm-12">
                      <div style={{ padding: '24px', textAlign: 'center' }}>
                        <p style={{ color: '#6a6a6a', marginBottom: '16px' }}>No EIAP Clusters configured yet.</p>
                        <p style={{ color: '#6a6a6a' }}>
                          Please configure an EIAP Cluster in <Link to="/integrations/eiap-clusters">Endpoints</Link> first.
                        </p>
                      </div>
                    </div>
                  ) : (
                    eiapClusters.map((cluster) => (
                      <div key={cluster.id} className="column sm-12 md-6 lg-4">
                        <ReactEDSWrapper
                          component={Card}
                          title={cluster.name}
                          selected={formData.eiapCluster === cluster.id}
                          onChange={(selected) => {
                            if (selected) {
                              setFormData({ ...formData, eiapCluster: cluster.id, enms: [] });
                            } else {
                              setFormData({ ...formData, eiapCluster: '', enms: [] });
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                            <span>Category: {cluster.category}</span>
                          </div>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )}
                </div>

                {formData.eiapCluster && (
                  <div className="row">
                    <div className="column sm-12">
                      <h4>Select ENMs</h4>
                    </div>
                    {availableEnms.length === 0 ? (
                      <div className="column sm-12">
                        <div style={{ padding: '24px', textAlign: 'center' }}>
                          <p style={{ color: '#6a6a6a', marginBottom: '16px' }}>No ENMs linked to this EIAP Cluster yet.</p>
                          <p style={{ color: '#6a6a6a' }}>
                            Please link ENMs to this cluster in <Link to="/integrations/enms"> Endpoints</Link> first.
                          </p>
                        </div>
                      </div>
                    ) : (
                      availableEnms.map((enm) => (
                        <div key={enm.id} className="column sm-12 md-6 lg-4">
                          <ReactEDSWrapper
                            component={Card}
                            title={enm.name}
                            selected={formData.enms.includes(enm.id)}
                            onChange={(selected) => {
                              const updatedEnms = selected ? [...formData.enms, enm.id] : formData.enms.filter((e) => e !== enm.id);
                              setFormData({ ...formData, enms: updatedEnms });
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                              <i className="icon icon-antenna" style={{ marginRight: '8px', fontSize: '18px' }} />
                              <span>{enm.url}</span>
                            </div>
                            <p style={{ fontSize: '12px', color: '#6a6a6a' }}>{enm.description}</p>
                          </ReactEDSWrapper>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {preSelectedProductType === 'CNIS' && (
              <div>
                <h3>
                  Select CNIS Endpoint <span style={{ color: '#0069C2' }}> *</span>
                </h3>
                <p>Select the CNIS Endpoint where SW onboarding will execute.</p>

                <div className="row" style={{ marginBottom: '24px' }}>
                  <div className="column sm-12">
                    <h4>Select CNIS Endpoint</h4>
                  </div>
                  {cnis.length === 0 ? (
                    <div className="column sm-12">
                      <div style={{ padding: '24px', textAlign: 'center' }}>
                        <p style={{ color: '#6a6a6a', marginBottom: '16px' }}>No CNIS Endpoints configured yet.</p>
                        <p style={{ color: '#6a6a6a' }}>
                          Please configure a CNIS Endpoint in <Link to="/integrations/cnis">Endpoints</Link> first.
                        </p>
                      </div>
                    </div>
                  ) : (
                    cnis.map((c) => (
                      <div key={c.id} className="column sm-12 md-6 lg-4">
                        <ReactEDSWrapper
                          component={Card}
                          title={c.name}
                          selected={formData.cnisEndpoint === c.id}
                          onChange={(selected) => {
                            if (selected) {
                              setFormData({ ...formData, cnisEndpoint: c.id || '' });
                            } else {
                              setFormData({ ...formData, cnisEndpoint: '' });
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <i className="icon icon-link" style={{ marginRight: '8px', fontSize: '18px' }} />
                            <span>Endpoint: {c.endpoint}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                            <span>Category: {c.category}</span>
                          </div>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            {preSelectedProductType === 'Purpose Built' && (
              <div>
                <div className="row">
                  <div className="column sm-12">
                    <h4>Select ENMs</h4>
                  </div>
                  {enms.length === 0 ? (
                    <div className="column sm-12">
                      <div style={{ padding: '24px', textAlign: 'center' }}>
                        <p style={{ color: '#6a6a6a', marginBottom: '16px' }}>No ENMs linked to this EIAP Cluster yet.</p>
                        <p style={{ color: '#6a6a6a' }}>
                          Please link ENMs to this cluster in <Link to="/integrations/enms"> Endpoints</Link> first.
                        </p>
                      </div>
                    </div>
                  ) : (
                    enms.map((enm) => (
                      <div key={enm.id} className="column sm-12 md-6 lg-4">
                        <ReactEDSWrapper
                          component={Card}
                          title={enm.name}
                          selected={formData.enms.includes(enm.id)}
                          onChange={(selected) => {
                            const updatedEnms = selected ? [...formData.enms, enm.id] : formData.enms.filter((e) => e !== enm.id);
                            handleSelectEnm(updatedEnms);
                            setFormData({ ...formData, enms: updatedEnms });
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            <i className="icon icon-antenna" style={{ marginRight: '8px', fontSize: '18px' }} />
                            <span>{enm.url}</span>
                          </div>
                          <p style={{ fontSize: '12px', color: '#6a6a6a' }}>{enm.description}</p>
                        </ReactEDSWrapper>
                      </div>
                    ))
                  )}
                </div>
                {formData.enms?.length > 0 && (
                  <div className="row" style={{ marginBottom: '24px' }}>
                    <div className="column sm-12">
                      <h4>Select Moshell</h4>
                    </div>
                    {enmMoshells.length === 0 ? (
                      <div className="column sm-12">
                        <div style={{ padding: '24px', textAlign: 'center' }}>
                          <p style={{ color: '#6a6a6a', marginBottom: '16px' }}>No Moshell configured yet.</p>
                          <p style={{ color: '#6a6a6a' }}>
                            Please configure an Moshell Endpoint in <Link to="/integrations/moshell">Endpoints</Link> first.
                          </p>
                        </div>
                      </div>
                    ) : (
                      enmMoshells.map((moshell) => (
                        <div key={moshell.id} className="column sm-12 md-6 lg-4">
                          <ReactEDSWrapper
                            component={Card}
                            title={moshell.name}
                            selected={formData.moshell === moshell.id}
                            onChange={(selected) => {
                              if (selected && moshell.id) {
                                setFormData({ ...formData, moshell: moshell.id, pbNodesDeploy: [] });
                                getMoshellNodes(moshell.id);
                              } else {
                                setFormData({ ...formData, moshell: '' });
                                getMoshellNodes('');
                              }
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                              <i className="icon icon-link" style={{ marginRight: '8px', fontSize: '18px' }} />
                              <span>Endpoint: {moshell.endpoint}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                              <i className="icon icon-cloud" style={{ marginRight: '8px', fontSize: '18px' }} />
                              <span>Category: {moshell.category}</span>
                            </div>
                          </ReactEDSWrapper>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </ReactEDSWrapper>

          {/* Step 4: Selective Onboarding */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Onboarding">
            <h3>
              Configure Selective Onboarding <span style={{ color: '#0069C2' }}> *</span>
            </h3>
            <p>
              When enabled, the pipeline pauses at Artifacts Retrieval/Onboarding for a go/no-go decision. When disabled, it runs without
              pausing (automated).
            </p>
            <div className="row">
              <div className="column sm-12">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <label style={{ fontWeight: '500', minWidth: '180px' }}>Enable Selective Onboarding:</label>
                  <ReactEDSWrapper
                    component={Switch}
                    checked={formData.selectiveOnboarding.enabled}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData({
                        ...formData,
                        selectiveOnboarding: {
                          enabled: e.target.checked
                        }
                      })
                    }
                    enabledLabel="On"
                    disabledLabel="Off"
                  />
                </div>
                <p style={{ fontSize: '0.875rem', color: '#6a6a6a', marginTop: '-8px', marginLeft: '180px' }}>
                  {formData.selectiveOnboarding.enabled
                    ? 'Pipeline will pause at Artifacts stages and wait for operator go/no-go decision.'
                    : 'Pipeline will run continuously without pausing.'}
                </p>
              </div>
            </div>
          </ReactEDSWrapper>

          {/* Step 5 & 6: Deployment & Test Automation (Full mode only) */}
          {[
            isFullMode ? (
              <ReactEDSWrapper key="deployment" component={WizardStep} stepTitle="Deployment" valid={validateStep5()}>
                <div className="row">
                  <div className={preSelectedProductType === 'Purpose Built' ? 'column lg-4 sm-12' : 'column sm-12'}>
                    <div style={{ gap: '12px', marginBottom: '16px' }}>
                      <h4>
                        Configure Deployment Trigger <span style={{ color: '#0069C2' }}> *</span>
                      </h4>
                      <p>
                        Choose whether the deployment should start manually or automatically. Auto-Deploy On, automatically triggers the
                        deployment stage.
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <label style={{ fontWeight: '500', minWidth: '150px' }}>Auto-Deploy:</label>
                      <ReactEDSWrapper
                        component={Switch}
                        checked={formData.deploymentAutomation}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormData({ ...formData, deploymentAutomation: e.target.checked })
                        }
                        enabledLabel="On"
                        disabledLabel="Off"
                      />
                    </div>
                    {preSelectedProductType == 'Purpose Built' && (
                      <div>
                        <h4>
                          Upgrade Method Selection <span style={{ color: '#0069C2' }}> *</span>
                        </h4>
                        {/* <p>Choose ASU or Moshell solution for PB-RAN upgrade.</p> */}
                        <div className="row" style={{ marginBottom: '24px' }}>
                          <fieldset>
                            <div className="column sm-12">
                              <input id="rbtn1" name="group1" type="radio" onChange={() => setUpgradeMethod('asu')} disabled />
                              <label htmlFor="rbtn1">ASU (Automatic Software Upgrade)</label>
                            </div>
                            <div className="column sm-12">
                              <input id="rbtn2" name="group1" type="radio" onChange={() => setUpgradeMethod('moshell')} checked />
                              <label htmlFor="rbtn2">Moshell</label>
                            </div>
                          </fieldset>
                        </div>
                      </div>
                    )}
                  </div>
                  {preSelectedProductType === 'Purpose Built' && (
                    <div className="column lg-8 sm-12">
                      {upgradeMethod === 'moshell' && (
                        <>
                          <div style={{ gap: '12px', marginBottom: '16px' }}>
                            <h4>Select RAN Nodes</h4>
                            <p>Please select the nodes to be upgraded.</p>
                          </div>
                          {formData.moshell && (
                            <MoshellNodesList
                              moshellId={formData.moshell}
                              nodes={moshellNodes}
                              savedNodes={formData.pbNodesDeploy}
                              selectNodes={handleSelectNodes}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </ReactEDSWrapper>
            ) : null,

            isFullMode ? (
              <ReactEDSWrapper key="test" component={WizardStep} stepTitle="Testing">
                <h3>
                  Configure Test Trigger <span style={{ color: '#0069C2' }}> *</span>
                </h3>
                <p>
                  Choose whether the testing should start manually or automatically. Auto-Test On, automatically triggers the test stage.
                </p>
                <div className="row">
                  <div className="column sm-12">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                      <label style={{ fontWeight: '500', minWidth: '150px' }}>Auto-Test:</label>
                      <ReactEDSWrapper
                        component={Switch}
                        checked={formData.testAutomation}
                        disabled={preSelectedProductType !== 'gNB' && preSelectedProductType !== 'rApps'}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormData({ ...formData, testAutomation: e.target.checked })
                        }
                        enabledLabel="On"
                        disabledLabel="Off"
                      />
                    </div>
                  </div>
                </div>
              </ReactEDSWrapper>
            ) : null
          ].filter(Boolean)}

          {/* Review & Create */}
          <ReactEDSWrapper component={WizardStep} stepTitle="Review & Create">
            <h4>Review Your Automation Flow Configuration</h4>
            <div className="row" style={{ marginTop: '24px' }}>
              <div className="column sm-12">
                <ReactEDSWrapper component={Card} title="Configuration Summary">
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div>
                      <strong>Automation Flow Name:</strong> {formData.flowName}
                    </div>
                    <div>
                      <strong>SW Repository:</strong>{' '}
                      {formData.repository === 'swgw'
                        ? 'Ericsson Software Gateway'
                        : formData.repository
                          ? repositories.find((r) => r.id === formData.repository)?.name || 'Not configured'
                          : 'Not configured'}
                    </div>
                    <div>
                      <strong>Product Type:</strong> {preSelectedProductType || 'Not selected'}
                    </div>
                    <div>
                      <strong>Product Lines:</strong>{' '}
                      {productLines.length > 0 ? productLines.map((pl) => pl.name).join(', ') : 'None mapped'}
                    </div>
                    {preSelectedProductType !== 'CNIS' && (
                      <>
                        <div>
                          <strong>EIAP Cluster:</strong>{' '}
                          {formData.eiapCluster
                            ? eiapClusters.find((c) => c.id === formData.eiapCluster)?.name || 'Not selected'
                            : 'Not selected'}
                        </div>
                        <div>
                          <strong>ENMs:</strong>{' '}
                          {formData.enms.length > 0
                            ? formData.enms
                                .map((enmId) => enms.find((e) => e.id === enmId)?.name)
                                .filter(Boolean)
                                .join(', ')
                            : 'None selected'}
                        </div>
                      </>
                    )}
                    {preSelectedProductType === 'CNIS' && (
                      <div>
                        <strong>CNIS Endpoint:</strong>{' '}
                        {formData.cnisEndpoint ? cnis.find((c) => c.id === formData.cnisEndpoint)?.name || 'Not selected' : 'Not selected'}
                      </div>
                    )}
                    <div>
                      <strong>Selective Onboarding:</strong> {formData.selectiveOnboarding.enabled ? 'Enabled' : 'Disabled'}
                    </div>
                    {isFullMode && (
                      <>
                        <div>
                          <strong>Auto-Deploy:</strong> {formData.deploymentAutomation ? 'On' : 'Off'}
                        </div>
                        <div>
                          <strong>Auto-Test:</strong> {formData.testAutomation ? 'On' : 'Off'}
                        </div>
                      </>
                    )}
                  </div>
                </ReactEDSWrapper>
              </div>
            </div>
          </ReactEDSWrapper>
        </ReactEDSWrapper>
      </ReactEDSWrapper>
    </div>
  );
}
